import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Edit, Trash2, Package, Camera, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../utils/config';

const API = getApiUrl();

// Photo Upload Component
const PhotoUpload = ({ onPhotoSelect, existingPhoto = null, className = "", multiple = false }) => {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(existingPhoto?.data ? `data:${existingPhoto.content_type};base64,${existingPhoto.data}` : null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      const base64 = await convertToBase64(file);
      const photoData = {
        filename: file.name,
        data: base64.split(',')[1], // Remove data:image/jpeg;base64, prefix
        content_type: file.type,
        size: file.size
      };

      setPreview(base64);
      onPhotoSelect(photoData);
    } catch (error) {
      toast.error('Failed to process image');
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const removePhoto = () => {
    setPreview(null);
    onPhotoSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {preview ? (
        <div className="relative">
          <img 
            src={preview} 
            alt="Preview" 
            className="w-full h-32 object-cover rounded-lg border"
          />
          <Button
            onClick={removePhoto}
            variant="destructive"
            size="sm"
            className="absolute top-2 right-2"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
            isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">
            Drop image here or click to select
          </p>
          <p className="text-xs text-gray-400 mt-1">
            JPG, PNG up to 5MB
          </p>
        </div>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />
    </div>
  );
};

const PartsList = ({ car, isOwner, hasEditPermission, userPlan }) => {
  const [parts, setParts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [expandedParts, setExpandedParts] = useState(new Set()); // Track which parts are expanded
  const [formData, setFormData] = useState({
    category: '',
    part_name: '',
    brand: '',
    part_number: '',
    notes: '',
    installed_date: '',
    photos: []
  });

  const canEdit = isOwner || hasEditPermission;
  
  // Check if user has parts list access (Basic, Premium, or Professional)
  const hasPartsListAccess = userPlan === 'Basic' || userPlan === 'Premium' || userPlan === 'Professional';

  useEffect(() => {
    fetchParts();
    fetchCategories();
  }, [car.id]);

  const fetchParts = async () => {
    try {
      const response = await axios.get(`${API}/cars/${car.id}/parts`);
      setParts(response.data);
    } catch (error) {
      console.error('Error fetching parts:', error);
      toast.error('Failed to load parts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/part-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Provide default categories if API fails
      setCategories([
        { value: "Engine Oil", label: "Engine Oil" },
        { value: "Oil Filter", label: "Oil Filter" },
        { value: "Air Filter", label: "Air Filter" },
        { value: "Cabin Filter", label: "Cabin Filter" },
        { value: "Fuel Filter", label: "Fuel Filter" },
        { value: "Coolant", label: "Coolant" },
        { value: "Brake Fluid", label: "Brake Fluid" },
        { value: "Transmission Fluid", label: "Transmission Fluid" },
        { value: "Spark Plugs", label: "Spark Plugs" },
        { value: "Battery", label: "Battery" },
        { value: "Tires", label: "Tires" },
        { value: "Brake Pads", label: "Brake Pads" },
        { value: "Wiper Blades", label: "Wiper Blades" },
        { value: "Other", label: "Other" }
      ]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user has access before proceeding
    if (!hasPartsListAccess) {
      toast.error('Parts List is available on Basic plan and above. Upgrade to Basic or Premium to manage your parts inventory.', {
        duration: 5000,
        icon: '🔒',
      });
      return;
    }
    
    if (!formData.category || !formData.part_name) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (editingPart) {
        // Update existing part
        const response = await axios.put(`${API}/cars/${car.id}/parts/${editingPart.id}`, formData);
        setParts(parts.map(p => p.id === editingPart.id ? response.data : p));
        toast.success('Part updated successfully!');
        setShowEditDialog(false);
      } else {
        // Create new part
        const response = await axios.post(`${API}/cars/${car.id}/parts`, formData);
        setParts([...parts, response.data]);
        toast.success('Part added successfully!');
        setShowAddDialog(false);
      }
      resetForm();
    } catch (error) {
      console.error('Error saving part:', error);
      // Check if it's a feature restriction error
      if (error.response?.status === 403 && error.response?.data?.detail?.error === 'feature_restricted') {
        const errorDetail = error.response.data.detail;
        toast.error(errorDetail.message || 'Parts List is available on Basic plan and above.', {
          duration: 5000,
        });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save part');
      }
    }
  };

  const handleDelete = async (partId) => {
    if (!window.confirm('Are you sure you want to delete this part?')) return;

    try {
      await axios.delete(`${API}/cars/${car.id}/parts/${partId}`);
      setParts(parts.filter(p => p.id !== partId));
      toast.success('Part deleted successfully');
    } catch (error) {
      console.error('Error deleting part:', error);
      toast.error('Failed to delete part');
    }
  };

  const handleEdit = (part) => {
    setEditingPart(part);
    setFormData({
      category: part.category || '',
      part_name: part.part_name || '',
      brand: part.brand || '',
      part_number: part.part_number || '',
      notes: part.notes || '',
      installed_date: part.installed_date || '',
      photos: part.photos || []
    });
    setShowEditDialog(true);
  };

  const handlePhotoSelect = (photoData) => {
    if (photoData) {
      setFormData(prev => ({
        ...prev,
        photos: [photoData] // Single photo for now
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        photos: []
      }));
    }
  };

  const togglePartExpansion = (partId) => {
    const newExpanded = new Set(expandedParts);
    if (newExpanded.has(partId)) {
      newExpanded.delete(partId);
    } else {
      newExpanded.add(partId);
    }
    setExpandedParts(newExpanded);
  };

  const resetForm = () => {
    setFormData({
      category: '',
      part_name: '',
      brand: '',
      part_number: '',
      notes: '',
      installed_date: '',
      photos: []
    });
    setEditingPart(null);
  };

  if (loading) {
    return <div>Loading parts...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold flex items-center">
          <Package className="w-5 h-5 mr-2" />
          My Parts List
        </h3>
        {canEdit && (
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-1" />
                Add Part
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Part</DialogTitle>
              </DialogHeader>
              
              {/* Upgrade Banner for Free Users */}
              {!hasPartsListAccess && (
                <div className="mb-4 p-4 bg-orange-50 border-2 border-orange-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                        <span className="text-xl">🔒</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-orange-900 mb-1">Upgrade to Basic or Premium Required</h4>
                      <p className="text-sm text-orange-700">
                        Parts List is available on Basic plan and above. Upgrade your subscription to track parts, maintenance items, and upgrades.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="category">Category *</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(value) => setFormData({...formData, category: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="part_name">Part Name *</Label>
                    <Input
                      id="part_name"
                      value={formData.part_name}
                      onChange={(e) => setFormData({...formData, part_name: e.target.value})}
                      placeholder="e.g., Castrol EDGE 5W-30"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="brand">Brand</Label>
                    <Input
                      id="brand"
                      value={formData.brand}
                      onChange={(e) => setFormData({...formData, brand: e.target.value})}
                      placeholder="e.g., Bosch, Castrol"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="part_number">Part Number</Label>
                    <Input
                      id="part_number"
                      value={formData.part_number}
                      onChange={(e) => setFormData({...formData, part_number: e.target.value})}
                      placeholder="e.g., 0986AF1234"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="installed_date">Installed Date</Label>
                    <Input
                      id="installed_date"
                      type="date"
                      value={formData.installed_date}
                      onChange={(e) => setFormData({...formData, installed_date: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Additional notes about this part..."
                    className="w-full min-h-[80px] p-2 border rounded-md"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Part Photo</Label>
                  <PhotoUpload
                    onPhotoSelect={handlePhotoSelect}
                    existingPhoto={formData.photos[0]}
                  />
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button type="button" variant="outline" onClick={() => {
                    setShowAddDialog(false);
                    resetForm();
                  }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    Add Part
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}

        {/* Edit Dialog */}
        {canEdit && (
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Part</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-category">Category *</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(value) => setFormData({...formData, category: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-part_name">Part Name *</Label>
                    <Input
                      id="edit-part_name"
                      value={formData.part_name}
                      onChange={(e) => setFormData({...formData, part_name: e.target.value})}
                      placeholder="e.g., Castrol EDGE 5W-30"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-brand">Brand</Label>
                    <Input
                      id="edit-brand"
                      value={formData.brand}
                      onChange={(e) => setFormData({...formData, brand: e.target.value})}
                      placeholder="e.g., Bosch, Castrol"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-part_number">Part Number</Label>
                    <Input
                      id="edit-part_number"
                      value={formData.part_number}
                      onChange={(e) => setFormData({...formData, part_number: e.target.value})}
                      placeholder="e.g., 0986AF1234"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-installed_date">Installed Date</Label>
                    <Input
                      id="edit-installed_date"
                      type="date"
                      value={formData.installed_date}
                      onChange={(e) => setFormData({...formData, installed_date: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-notes">Notes</Label>
                  <textarea
                    id="edit-notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Additional notes about this part..."
                    className="w-full min-h-[80px] p-2 border rounded-md"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Part Photo</Label>
                  <PhotoUpload
                    onPhotoSelect={handlePhotoSelect}
                    existingPhoto={formData.photos[0]}
                  />
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button type="button" variant="outline" onClick={() => {
                    setShowEditDialog(false);
                    resetForm();
                  }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    Update Part
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {parts.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center text-gray-500">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p>No parts recorded yet</p>
            {canEdit && <p className="text-sm mt-2">Click "Add Part" to start tracking your installed parts</p>}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {parts.map(part => {
            const isExpanded = expandedParts.has(part.id);
            return (
              <Card key={part.id} className="border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:shadow-md transition-all duration-200">
                {/* Collapsed Header - Always Visible */}
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                  onClick={() => togglePartExpansion(part.id)}
                >
                  <div className="flex items-center gap-3 flex-1">
                    {/* Part Category Icon */}
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Package className="w-4 h-4 text-blue-600" />
                    </div>
                    
                    {/* Part Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-card-foreground">{part.part_name}</h4>
                        {part.brand && (
                          <span className="text-sm text-card-foreground opacity-80">({part.brand})</span>
                        )}
                      </div>
                      <p className="text-sm text-card-foreground opacity-70">{part.category}</p>
                    </div>
                    
                    {/* Installation Date Badge */}
                    {part.installed_date && (
                      <div className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground font-medium">
                        {new Date(part.installed_date).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  
                  {/* Action Buttons and Expand Arrow */}
                  <div className="flex items-center gap-2">
                    {canEdit && (
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(part)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(part.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    
                    {/* Expand/Collapse Arrow */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600"
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
                    <div className="pt-4 space-y-3">
                      {/* Part Details Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {part.part_number && (
                          <div>
                            <span className="text-xs font-bold text-card-foreground opacity-75 uppercase tracking-wide">Part Number</span>
                            <p className="text-sm text-card-foreground font-medium mt-1">{part.part_number}</p>
                          </div>
                        )}
                        
                        {part.brand && (
                          <div>
                            <span className="text-xs font-bold text-card-foreground opacity-75 uppercase tracking-wide">Brand</span>
                            <p className="text-sm text-card-foreground font-medium mt-1">{part.brand}</p>
                          </div>
                        )}
                        
                        {part.installed_date && (
                          <div>
                            <span className="text-xs font-bold text-card-foreground opacity-75 uppercase tracking-wide">Installation Date</span>
                            <p className="text-sm text-card-foreground font-medium mt-1">
                              {new Date(part.installed_date).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                        
                        <div>
                          <span className="text-xs font-bold text-card-foreground opacity-75 uppercase tracking-wide">Category</span>
                          <p className="text-sm text-card-foreground font-medium mt-1">{part.category}</p>
                        </div>
                      </div>
                      
                      {/* Notes */}
                      {part.notes && (
                        <div>
                          <span className="text-xs font-bold text-card-foreground opacity-75 uppercase tracking-wide">Notes</span>
                          <p className="text-sm text-card-foreground font-medium mt-1 bg-muted/50 p-3 rounded-md border border-border">
                            {part.notes}
                          </p>
                        </div>
                      )}
                      
                      {/* Photos */}
                      {part.photos && part.photos.length > 0 && (
                        <div>
                          <span className="text-xs font-bold text-card-foreground opacity-75 uppercase tracking-wide">Photos</span>
                          <div className="flex gap-3 mt-2 flex-wrap">
                            {part.photos.map((photo, index) => (
                              <div key={photo.id || index} className="relative group">
                                <img
                                  src={photo.data ? `data:${photo.content_type};base64,${photo.data}` : photo.url}
                                  alt={photo.filename || 'Part photo'}
                                  className="w-20 h-20 object-cover rounded-lg border-2 border-gray-200 cursor-pointer hover:border-blue-300 transition-colors shadow-sm"
                                  onClick={() => {
                                    // Open photo in new window for larger view
                                    const newWindow = window.open();
                                    const imgSrc = photo.data ? `data:${photo.content_type};base64,${photo.data}` : photo.url;
                                    newWindow.document.write(`<img src="${imgSrc}" style="max-width: 100%; height: auto;" />`);
                                  }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all duration-200 flex items-center justify-center">
                                  <Camera className="w-5 h-5 text-white opacity-0 group-hover:opacity-70 transition-opacity" />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PartsList;
